import { Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import dischargeService, { Discharge, DischargeStatus } from '../services/discharge.service';
import { StatsCard } from './shared/statscard';

interface PostDischargePatient {
  id: string;
  patientId?: string;
  name: string;
  avatar: string;
  dischargeDate: string;
  riskLevel: 'High' | 'Medium' | 'Low';
  followUpDue: string;
  lastContact: string;
  specialty: string;
  careManager: string;
  status: string;
}

const getRiskColor = (level: string) => {
  switch (level) {
    case 'High': return '#EE0F12';
    case 'Medium': return '#F6AD55';
    case 'Low': return '#1FB551';
    default: return '#A0AEC0';
  }
};

export const PostDischargeLayout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [patients, setPatients] = useState<PostDischargePatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    riskLevel: '',
    specialty: '',
    careManager: '',
    search: ''
  });
  const [, setStats] = useState<any>(null);



  useEffect(() => {
    fetchDischarges();
    fetchStats();
  }, []);

  const fetchDischarges = async () => {
    try {
      setLoading(true);
      const discharges = await dischargeService.getAllDischarges();
      const transformedPatients: PostDischargePatient[] = discharges.map((discharge: Discharge) => {
        const patientName = discharge.patient
          ? `${discharge.patient.first_name} ${discharge.patient.last_name}`
          : 'Unknown Patient';
        const initials = discharge.patient
          ? `${discharge.patient.first_name?.[0] || ''}${discharge.patient.last_name?.[0] || ''}`
          : 'UN';
        return {
          id: discharge.id,
          patientId: discharge.patient?.id || (discharge as any).patient_id || null,
          name: patientName,
          avatar: initials,
          dischargeDate: new Date(discharge.discharge_date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          riskLevel: discharge.risk_level as 'High' | 'Medium' | 'Low',
          followUpDue: discharge.next_followup_due
            ? new Date(discharge.next_followup_due).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })
            : 'Not set',
          lastContact: discharge.updated_at
            ? new Date(discharge.updated_at).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })
            : '-',
          specialty: discharge.specialty || 'General',
          careManager: discharge.care_manager || 'Unassigned',
          status: discharge.status
        };
      });
      setPatients(transformedPatients);
    } catch (error) {
      console.error('Failed to fetch discharges:', error);
      toast.error('Failed to load post-discharge patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const s = await dischargeService.getDischargeStats();
      setStats(s);
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  };

  const filteredPatients = patients.filter(p => {
    const risk = (p.riskLevel || '').toString().toLowerCase();
    const specialty = (p.specialty || '').toString().toLowerCase();
    const careManager = (p.careManager || '').toString().toLowerCase();
    const name = (p.name || '').toString().toLowerCase();

    if (filters.riskLevel && risk !== (filters.riskLevel || '').toString().toLowerCase()) return false;
    if (filters.specialty && specialty !== (filters.specialty || '').toString().toLowerCase()) return false;
    if (filters.careManager && careManager !== (filters.careManager || '').toString().toLowerCase()) return false;
    if (filters.search && !name.includes((filters.search || '').toString().toLowerCase())) return false;
    return true;
  });

  const dischargePatients = Array.from(
    patients.reduce((map, p) => {
      const patientId = p.patientId || p.id;
      if (map.has(patientId)) {
        const existing = map.get(patientId);
        if (existing) {
          existing.dischargeCount += 1;
        }
      } else {
        map.set(patientId, {
          id: patientId,
          name: p.name,
          username: p.name,
          dischargeCount: 1,
          latestDischargeDate: p.dischargeDate
        });
      }
      return map;
    }, new Map()).values()
  );

  const filteredModalPatients = dischargePatients.filter((p: any) => {
    if (!search) return true;
    const q = (search || '').toString().toLowerCase();
    const name = (p.name || '').toString().toLowerCase();
    const username = (p.username || '').toString().toLowerCase();
    return name.includes(q) || username.includes(q);
  });

  const handleToggle = (patient_id: string) => {
    setSelectedPatients((prev) =>
      prev.includes(patient_id) ? prev.filter((x) => x !== patient_id) : [...prev, patient_id]
    );
  };

  const handleConfirm = async () => {
    if (!selectedPatients.length) {
      alert('Please select at least one patient.');
      return;
    }

    try {
      const BASE_URL = 'https://vivotal-backend.vercel.app/api/v1';
      const res = await fetch(`${BASE_URL}/patients/map-to-provider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ provider_id: user?.id, patient_ids: selectedPatients }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Mapping failed');

      toast.success('Patients mapped successfully');
      setOpen(false);
      setSelectedPatients([]);
    } catch (err: any) {
      console.error('Mapping error:', err);
      toast.error(err.message || 'Something went wrong while mapping.');
    }
  };

  const dynamicStats = {
    total: filteredPatients.length,
    active: filteredPatients.filter(p => p.status === DischargeStatus.ACTIVE).length,
    recent: filteredPatients.filter(p => {
      const d = new Date(p.dischargeDate);
      const daysDiff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff <= 7;
    }).length,
    overdue: filteredPatients.filter(p => {
      if (!p.followUpDue || p.followUpDue === 'Not set') return false;
      const f = new Date(p.followUpDue);
      return f < new Date();
    }).length
  };

  const handleRowClick = (dischargeId: string) => {
    navigate(`/layout/discharge/${dischargeId}`);
  };

  useEffect(() => {
    setPage(0);
  }, [filters]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="section" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div className="heading">Post-Discharge Patients</div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button className="outline-bttn">Export</button>
          <button className="success-bttn" onClick={() => setOpen(true)}>+ Add Patients</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        <StatsCard
          count={String(dynamicStats.active).padStart(2, '0')}
          label={`${dynamicStats.active} active post-discharge patients`}
          icon={null}
          color="#F6AD55"
        />
        <StatsCard
          count={String(dynamicStats.recent).padStart(2, '0')}
          label="Recently discharged and added"
          icon={null}
          color="#1FB551"
        />
        <StatsCard
          count={String(dynamicStats.overdue).padStart(2, '0')}
          label="Patients with Overdue Follow-ups"
          icon={null}
          color="#EE0F12"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search patient.."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.riskLevel}
              onChange={(e) => setFilters(prev => ({ ...prev, riskLevel: e.target.value }))}
              displayEmpty
            >
              <MenuItem value="">Risk Level</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select defaultValue="" displayEmpty>
              <MenuItem value="">Follow-up Due Status</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.specialty}
              onChange={(e) => setFilters(prev => ({ ...prev, specialty: e.target.value }))}
              displayEmpty
            >
              <MenuItem value="">All Specialties</MenuItem>
              {Array.from(new Set(patients.map(p => p.specialty))).map(specialty => (
                <MenuItem key={specialty} value={specialty}>{specialty}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.careManager}
              onChange={(e) => setFilters(prev => ({ ...prev, careManager: e.target.value }))}
              displayEmpty
            >
              <MenuItem value="">All Managers</MenuItem>
              {Array.from(new Set(patients.map(p => p.careManager))).map(manager => (
                <MenuItem key={manager} value={manager}>{manager}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <Box sx={{ background: '#fff', borderRadius: '8px', border: '1px solid rgba(226, 232, 240, 0.5)', overflow: 'hidden' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(226, 232, 240, 0.5)' }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>List</span>
            <span style={{ fontSize: '12px', color: '#A0AEC0' }}>Total Patients: {filteredPatients.length}</span>
          </div>
        </div>

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Patient Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Discharge Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Risk Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Follow-up Due</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Last Contact</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Specialty</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Care Manager</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>Discharge Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPatients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#A0AEC0' }}>
                  No discharge records found
                </TableCell>
              </TableRow>
            ) : (
              filteredPatients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((p: PostDischargePatient) => (
                <TableRow
                  key={p.id}
                  hover
                  onClick={() => handleRowClick(p.id)}
                  sx={{
                    '&:hover': { backgroundColor: '#f0f4f8' },
                    cursor: 'pointer'
                  }
                  }>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.id.slice(0, 8)}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '12px', backgroundColor: '#cbd5e0' }}>
                      {p.avatar}
                    </Avatar>
                    {p.name}
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.dischargeDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.riskLevel}
                      size="small"
                      sx={{
                        backgroundColor: getRiskColor(p.riskLevel),
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '11px',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.followUpDue}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.lastContact}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.specialty}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.careManager}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: '#222' }}>{p.status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid rgba(226, 232, 240, 0.5)', backgroundColor: '#fafbfc' }}>
          <div style={{ fontSize: '12px', color: '#A0AEC0' }}>
            Rows per page:{' '}
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value));
                setPage(0);
              }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 500, color: '#222' }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div style={{ fontSize: '12px', color: '#A0AEC0' }}>
            {filteredPatients.length === 0
              ? '0-0 of 0'
              : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, filteredPatients.length)} of ${filteredPatients.length}`
            }
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              style={{
                border: '1px solid #e2e8f0',
                background: page === 0 ? '#f7fafc' : '#fff',
                padding: '6px 8px',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                opacity: page === 0 ? 0.5 : 1
              }}
            >
              ‹
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(filteredPatients.length / rowsPerPage) - 1}
              style={{
                border: '1px solid #e2e8f0',
                background: page >= Math.ceil(filteredPatients.length / rowsPerPage) - 1 ? '#f7fafc' : '#fff',
                padding: '6px 8px',
                cursor: page >= Math.ceil(filteredPatients.length / rowsPerPage) - 1 ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                opacity: page >= Math.ceil(filteredPatients.length / rowsPerPage) - 1 ? 0.5 : 1
              }}
            >
              ›
            </button>
          </div>
        </div>
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Box>
            <Typography variant="h6" fontWeight="bold">Select Patients with Discharge Records</Typography>
            <Typography variant="body2" color="textSecondary" mt={0.5}>
              {dischargePatients.length} patients with active discharge records
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <input
            type="text"
            placeholder="Search patient by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "16px",
              border: "1px solid #ccc",
              borderRadius: "6px",
            }}
          />

          {dischargePatients.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No patients with discharge records found
              </Typography>
            </Box>
          )}

          {dischargePatients.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: "12px 10px", fontWeight: 600, fontSize: '13px', color: '#666' }}>Select</th>
                  <th style={{ padding: "12px 10px", fontWeight: 600, fontSize: '13px', color: '#666' }}>Patient Name</th>
                  <th style={{ padding: "12px 10px", fontWeight: 600, fontSize: '13px', color: '#666' }}>Status</th>
                  <th style={{ padding: "12px 10px", fontWeight: 600, fontSize: '13px', color: '#666' }}>Discharge Count</th>
                </tr>
              </thead>

              <tbody>
                {filteredModalPatients.map((p: any) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "10px" }}>
                      <Checkbox checked={selectedPatients.includes(p.id)} onChange={() => handleToggle(p.id)} />
                    </td>

                    <td style={{ padding: "10px", fontSize: '14px' }}>{p.name}</td>

                    <td style={{ padding: "10px" }}>
                      <Chip
                        label="Has Discharge"
                        size="small"
                        sx={{
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          fontWeight: 600,
                          fontSize: '11px'
                        }}
                      />
                    </td>

                    <td style={{ padding: "10px", fontSize: '14px', color: '#666' }}>
                      {p.dischargeCount} {p.dischargeCount === 1 ? 'record' : 'records'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm}>
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}

